import { useCancelInboundSubscription } from "@/api";
import { toast } from "@/components/ui/use-toast";
import { SubscriptionTable } from "../../../types/subscriptions.type";
import { ConfirmDialog } from "./ConfirmDialog";

interface Props {
  subscription: SubscriptionTable & { type: "inbound" };
  open: boolean;
  setOpen: (v: boolean) => void;
}

export const CancelInboundSubscriptionDialog = ({
  open,
  setOpen,
  subscription
}: Props) => {
  const [cancelInbound, { loading }] = useCancelInboundSubscription();

  const handleCancelSubscription = async () => {
    try {
      const variables = { subscriptionId: subscription.id };

      const res = await cancelInbound({ variables });
      const errors = res.errors?.map((err) => err);
      const cancelledAppFragment = res.data?.subscriptionCancelInbound;

      if (errors?.length) {
        throw errors[0];
      } else if (!cancelledAppFragment) {
        throw new Error("No data");
      }

      switch (cancelledAppFragment.__typename) {
        case "AppSubscriptionInvalidStatus":
          toast({
            title: "Not cancelled",
            description: `Could not cancel the subscription with status: ${subscription.status}`,
            variant: "destructive"
          });
          break;
        case "AppSubscriptionNotFound":
          toast({
            title: "Not cancelled",
            description: `Could not find the subscription. Please report the issue`,
            variant: "destructive"
          });
          break;
        default:
          toast({
            title: "Subscription cancelled",
            description: `The subscription has successfully been cancelled!`
          });
          break;
      }
    } catch (error) {
      console.error(error);
      let errorMsg = error instanceof Error ? error.message : "Unknown error";
      toast({
        title: "Unexpected error",
        description: `An unexpected error occurred.
        Error: "${errorMsg}"
        Please report the issue`,
        variant: "destructive"
      });
    }
    setOpen(false);
  };

  return (
    <ConfirmDialog
      open={open}
      setOpen={setOpen}
      loading={loading}
      onConfirm={handleCancelSubscription}
      title="Cancel subscription?"
      description="This can not be undone"
    />
  );
};
